/**
 * Adding the ability to add data-on.ready, data-on.inviewport and
 * data-on.inviewport.{threshold}, data-on.click, data-on.mouseover,
 * data-on.mouseout, data-on.blur, data-on.input, data-on.change,
 * data-on.keydown, and data-on.keyup attributes to HTML elements so a function can
 * be executed upon DOMReady, when the element is in the viewport,
 * on click, on mouseover, on mouseout, on blur, on input, on change,
 * on keydown, or on keyup.
 *
 * The method needs to be in the window scope.
 *
 * MODIFIERS:
 * .once - Execute the handler only once (e.g., data-on.click.once="func1")
 * .prevent - Automatically call event.preventDefault() (e.g., data-on.click.prevent="func1")
 * .once.prevent or .prevent.once - Combine both modifiers
 *
 * When domcontent ready:
 * <div data-on.ready="func1"></div>
 * <div data-on.ready.once="func1"></div>
 *
 * When in viewport (100% visibility):
 * <div data-on.inviewport="Obj.method"></div>
 * <div data-on.inviewport.once="Obj.method"></div>
 *
 * When in 10% viewport:
 * <div data-on.inviewport.10="OtherObj.anotherMethod"></div>
 * <div data-on.inviewport.10.once="OtherObj.anotherMethod"></div>
 *
 * When clicked:
 * <div data-on.click="func1"></div>
 * <div data-on.click.once="func1"></div>
 * <div data-on.click.prevent="func1"></div>
 * <div data-on.click.once.prevent="func1"></div>
 *
 * When mouse enters element:
 * <div data-on.mouseover="func1"></div>
 * <div data-on.mouseover.once="func1"></div>
 *
 * When mouse leaves element:
 * <div data-on.mouseout="func1"></div>
 * <div data-on.mouseout.once="func1"></div>
 *
 * When element loses focus:
 * <input data-on.blur="func1">
 * <input data-on.blur.once="func1">
 *
 * When input value changes:
 * <input data-on.input="func1">
 * <input data-on.input.prevent="func1">
 *
 * When input value changes and loses focus:
 * <input data-on.change="func1">
 * <input data-on.change.once.prevent="func1">
 *
 * When key is pressed down:
 * <input data-on.keydown="func1">
 * <input data-on.keydown.prevent="func1">
 *
 * When key is released:
 * <input data-on.keyup="func1">
 * <input data-on.keyup.once="func1">
 *
 * Declare your function in the window scope:
 * func1 = function(el, event) {}
 * OR
 * window.func1 = function(el, event) {}
 * OR
 * CustomObj = {}
 * CustomObj.func1 = function(el, event){}
 * OR
 * window.CustomObj = {}
 * window.CustomObj.func1 = function(el, event){}
 */

var EventifyDOM = new Object();

EventifyDOM.thresholdObservers = {};
EventifyDOM.processedElements = new WeakSet(); // Track processed elements to prevent duplicates
EventifyDOM.functionCache = new Map(); // Cache resolved functions for better performance
EventifyDOM.mutationObserver = null; // Observer for dynamically added elements
EventifyDOM.eventListenersAdded = new Map(); // Track elements with event listeners to prevent duplicates
EventifyDOM.defaultVars = null; // Cache default vars to avoid repeated object creation
EventifyDOM.onceExecuted = new Map(); // Track elements that have executed once-only handlers

/**
 * Centralized selector for all data-on attributes including modifiers.
 * Used consistently across processAllAttributes and mutation observer.
 * Structured as an array for better maintainability.
 */
EventifyDOM.DATA_ON_SELECTORS = [
  // Base event types
  "[data-on\\.ready]",
  "[data-on\\.click]",
  "[data-on\\.mouseover]",
  "[data-on\\.mouseout]",
  "[data-on\\.blur]",
  "[data-on\\.input]",
  "[data-on\\.change]",
  "[data-on\\.keydown]",
  "[data-on\\.keyup]",

  // Viewport variants (all thresholds)
  '*[data-on^="data-on.inviewport"]',

  // Modifier variants: .once
  "[data-on\\.ready\\.once]",
  "[data-on\\.click\\.once]",
  "[data-on\\.mouseover\\.once]",
  "[data-on\\.mouseout\\.once]",
  "[data-on\\.blur\\.once]",
  "[data-on\\.input\\.once]",
  "[data-on\\.change\\.once]",
  "[data-on\\.keydown\\.once]",
  "[data-on\\.keyup\\.once]",

  // Modifier variants: .prevent
  "[data-on\\.ready\\.prevent]",
  "[data-on\\.click\\.prevent]",
  "[data-on\\.mouseover\\.prevent]",
  "[data-on\\.mouseout\\.prevent]",
  "[data-on\\.blur\\.prevent]",
  "[data-on\\.input\\.prevent]",
  "[data-on\\.change\\.prevent]",
  "[data-on\\.keydown\\.prevent]",
  "[data-on\\.keyup\\.prevent]",

  // Modifier variants: .once.prevent
  "[data-on\\.ready\\.once\\.prevent]",
  "[data-on\\.click\\.once\\.prevent]",
  "[data-on\\.mouseover\\.once\\.prevent]",
  "[data-on\\.mouseout\\.once\\.prevent]",
  "[data-on\\.blur\\.once\\.prevent]",
  "[data-on\\.input\\.once\\.prevent]",
  "[data-on\\.change\\.once\\.prevent]",
  "[data-on\\.keydown\\.once\\.prevent]",
  "[data-on\\.keyup\\.once\\.prevent]",

  // Modifier variants: .prevent.once
  "[data-on\\.ready\\.prevent\\.once]",
  "[data-on\\.click\\.prevent\\.once]",
  "[data-on\\.mouseover\\.prevent\\.once]",
  "[data-on\\.mouseout\\.prevent\\.once]",
  "[data-on\\.blur\\.prevent\\.once]",
  "[data-on\\.input\\.prevent\\.once]",
  "[data-on\\.change\\.prevent\\.once]",
  "[data-on\\.keydown\\.prevent\\.once]",
  "[data-on\\.keyup\\.prevent\\.once]",

  // Debug selectors (shouldn't match normally)
  '[class*="data-on\\."]',
  '[id*="data-on\\."]',
];

/**
 * Get the complete selector string by joining all selectors with commas.
 * Cached for performance.
 */
EventifyDOM.DATA_ON_SELECTOR = EventifyDOM.DATA_ON_SELECTORS.join(", ");

/**
 * Default variables for jQuery, instance, etc.
 * Falls back to vanilla JS if jQuery is unavailable.
 * Cached for performance to avoid repeated object creation.
 */
EventifyDOM.getDefaultVars = function () {
  if (!this.defaultVars) {
    this.defaultVars = {
      /**
       * this object
       */
      INSTANCE: this,

      /**
       * Use jQuery if available
       */
      $: window.jQuery || null,

      /**
       * Time between retries (milliseconds)
       */
      RETRY_DELAY: 300,

      /**
       * Maximum number of retries to check for function availability
       */
      MAX_RETRIES: 8,

      /**
       * If true removes attributes after handler is successfully attached
       */
      REMOVE_ATTRS_AFTER_ATTACHED: false,

      /**
       * If true renames attributes after handler is successfully attached (only applies if REMOVE_ATTRS_AFTER_ATTACHED is false)
       */
      RENAME_ATTRS_AFTER_ATTACHED: false,

      /**
       * Suffix to append when renaming attributes (default: '__attached')
       */
      ATTR_RENAME_SUFFIX: "__attached",
    };
  }
  return this.defaultVars;
};

/**
 * Parse attribute name to extract event type, modifiers, and threshold
 * Supports: data-on.{event}, data-on.{event}.once, data-on.{event}.prevent,
 * data-on.{event}.once.prevent, data-on.inviewport.{threshold}, etc.
 *
 * @param {string} attrName - The attribute name to parse
 * @returns {Object|null} - Parsed attribute info or null if invalid
 */
EventifyDOM.parseAttributeName = function (attrName) {
  if (!attrName.startsWith("data-on.")) return null;

  const parts = attrName.split(".");
  if (parts.length < 2) return null;

  const eventType = parts[1];
  let threshold = null;
  let hasOnce = false;
  let hasPrevent = false;

  // Check for modifiers and threshold
  for (let i = 2; i < parts.length; i++) {
    const part = parts[i];
    if (part === "once") {
      hasOnce = true;
    } else if (part === "prevent") {
      hasPrevent = true;
    } else if (eventType === "inviewport" && /^\d+$/.test(part)) {
      const thresholdPercent = parseFloat(part);
      if (!isNaN(thresholdPercent) && thresholdPercent >= 0 && thresholdPercent <= 100) {
        threshold = thresholdPercent / 100;
      }
    }
  }

  // Set default threshold for inviewport without specific threshold
  if (eventType === "inviewport" && threshold === null) {
    threshold = 0.001; // Use 0.001 instead of 0 to avoid the intersection observer bug
  }

  return {
    eventType,
    threshold,
    hasOnce,
    hasPrevent,
    isViewport: eventType === "inviewport",
  };
};

/**
 * Helper function to handle attribute cleanup after successful handler attachment
 * Consolidates the logic for removing, renaming, or leaving attributes unchanged
 *
 * @param {HTMLElement} el - The element to process
 * @param {string} attrName - The attribute name to process
 * @param {string} funcPath - The function path for reference
 */
EventifyDOM.handleAttributeAfterAttachment = function (el, attrName, funcPath) {
  var { INSTANCE, REMOVE_ATTRS_AFTER_ATTACHED, RENAME_ATTRS_AFTER_ATTACHED, ATTR_RENAME_SUFFIX } =
    this.getDefaultVars();

  if (REMOVE_ATTRS_AFTER_ATTACHED) {
    // Remove the attribute completely
    INSTANCE.removeAttr(el, attrName);
  } else if (RENAME_ATTRS_AFTER_ATTACHED) {
    // Rename the attribute with the specified suffix
    const newAttrName = attrName + ATTR_RENAME_SUFFIX;
    INSTANCE.setAttr(el, newAttrName, funcPath);
    INSTANCE.removeAttr(el, attrName);
  }
  // If both are false, leave the attribute unchanged
};

/**
 * Vanilla JS selector fallback if jQuery is unavailable.
 */
EventifyDOM.selectElements = function (selector) {
  const { $ } = this.getDefaultVars();
  return $ ? $(selector) : document.querySelectorAll(selector);
};

/**
 * Vanilla JS attribute getter fallback if jQuery is unavailable.
 */
EventifyDOM.getAttr = function (el, attr) {
  const { $ } = this.getDefaultVars();
  if ($ && el instanceof $) {
    return el.attr(attr); // jQuery method
  } else {
    return el.getAttribute(attr); // Vanilla JS method
  }
};

/**
 * Vanilla JS attribute setter fallback if jQuery is unavailable.
 */
EventifyDOM.setAttr = function (el, attr, value) {
  const { $ } = this.getDefaultVars();
  if ($ && el instanceof $) {
    el.attr(attr, value); // jQuery method
  } else {
    el.setAttribute(attr, value); // Vanilla JS method
  }
};

/**
 * Vanilla JS attribute remover fallback if jQuery is unavailable.
 */
EventifyDOM.removeAttr = function (el, attr) {
  const { $ } = this.getDefaultVars();
  if ($ && el instanceof $) {
    el.removeAttr(attr); // jQuery method
  } else {
    el.removeAttribute(attr); // Vanilla JS method
  }
};

/**
 * Convert NodeList to Array if it's not already array-like.
 * Cached conversion for better performance.
 */
EventifyDOM.toArray = function (collection) {
  return Array.isArray(collection) ? collection : Array.from(collection);
};

/**
 * Initialize the EventifyDOM. It runs on DOMReady and
 * triggers the ready, viewport, click, mouseover, mouseout, blur, input, change, keydown, and keyup detection.
 * Also sets up mutation observer for dynamically added elements.
 */
EventifyDOM.init = function () {
  if (typeof helpers !== "undefined" && helpers.console) {
    // helpers.console.prefix('EventifyDOM:');
    // helpers.console.log('EventifyDOM.init...!');
  }

  var { INSTANCE } = this.getDefaultVars();

  var callbackDelay = 1;

  var callback = function () {
    // Process all attributes in one pass for better performance
    INSTANCE.processAllAttributes();

    // Set up mutation observer for dynamically added elements
    INSTANCE.setupMutationObserver();
  };

  if (document.readyState === "complete" || document.readyState === "interactive") {
    setTimeout(callback, callbackDelay);
  } else {
    document.addEventListener("DOMContentLoaded", function () {
      setTimeout(callback, callbackDelay);
    });
  }
};

/**
 * OPTIMIZED: Process all data-on attributes in a single DOM pass
 * instead of separate queries for each attribute type.
 * Now supports .once and .prevent modifiers.
 */
EventifyDOM.processAllAttributes = function () {
  var { INSTANCE } = this.getDefaultVars();

  // Single query for all data-on elements (including modifiers)
  const allElements = INSTANCE.toArray(INSTANCE.selectElements(INSTANCE.DATA_ON_SELECTOR));

  // Group viewport elements by threshold for batch processing
  const viewportThresholdGroups = new Map();

  allElements.forEach(function (el) {
    const attributes = Array.from(el.attributes);

    attributes.forEach(function (attr) {
      if (!attr.name.startsWith("data-on.")) return;

      const parsedAttr = INSTANCE.parseAttributeName(attr.name);
      if (!parsedAttr) return;

      const funcPath = attr.value;
      if (!funcPath || !funcPath.trim()) return;

      if (parsedAttr.eventType === "ready") {
        // Process ready attribute (for ready, attachment = execution)
        if (!INSTANCE.processedElements.has(el)) {
          INSTANCE.processedElements.add(el);
          INSTANCE.retryFunctionCall(
            el,
            funcPath,
            function (success) {
              if (success) {
                // For ready events, attachment and execution are the same
                INSTANCE.handleAttributeAfterAttachment(el, attr.name, funcPath);
              }
            },
            0,
            null,
            parsedAttr,
          );
        }
      } else if (parsedAttr.isViewport) {
        // Process viewport attributes and group by threshold
        if (!viewportThresholdGroups.has(parsedAttr.threshold)) {
          viewportThresholdGroups.set(parsedAttr.threshold, []);
        }
        viewportThresholdGroups.get(parsedAttr.threshold).push({
          el,
          funcPath,
          attrName: attr.name,
          parsedAttr,
        });
      } else {
        // Process event listeners - prevent duplicates
        const eventKey = `${attr.name}`;
        if (!INSTANCE.eventListenersAdded.has(el)) {
          INSTANCE.eventListenersAdded.set(el, new Set());
        }
        if (!INSTANCE.eventListenersAdded.get(el).has(eventKey)) {
          INSTANCE.eventListenersAdded.get(el).add(eventKey);

          const success = INSTANCE.addEventListenerOnce(
            el,
            parsedAttr.eventType,
            funcPath,
            parsedAttr,
          );
          if (success) {
            INSTANCE.handleAttributeAfterAttachment(el, attr.name, funcPath);
          }
        }
      }
    });
  });

  // Create observers for viewport elements (batch processing)
  viewportThresholdGroups.forEach(function (elements, threshold) {
    INSTANCE.createObserver(threshold, elements);
  });
};

/**
 * OPTIMIZED: Add event listener with function binding in one step
 * Now supports .once and .prevent modifiers
 * Returns true if the event listener was successfully attached, false otherwise
 */
EventifyDOM.addEventListenerOnce = function (el, eventType, funcPath, parsedAttr) {
  var { INSTANCE } = this.getDefaultVars();

  // Check if the function exists before attaching the listener
  const resolvedFunc = INSTANCE.resolveFunction(funcPath);
  if (!resolvedFunc) {
    // Function doesn't exist yet, set up retry mechanism
    INSTANCE.retryEventListenerAttachment(el, eventType, funcPath, parsedAttr);
    return false; // Handler not attached yet
  }

  // Function exists, attach the listener immediately
  const handler = function (event) {
    // Handle .prevent modifier
    if (parsedAttr.hasPrevent && event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }

    // Handle .once modifier
    if (parsedAttr.hasOnce) {
      const onceKey = `${parsedAttr.eventType}_${funcPath}`;
      if (!INSTANCE.onceExecuted.has(el)) {
        INSTANCE.onceExecuted.set(el, new Set());
      }
      if (INSTANCE.onceExecuted.get(el).has(onceKey)) {
        return; // Already executed once, skip
      }
      INSTANCE.onceExecuted.get(el).add(onceKey);
    }

    INSTANCE.executeFunction(resolvedFunc, el, event);
  };

  el.addEventListener(eventType, handler, { passive: !parsedAttr.hasPrevent });

  return true; // Handler successfully attached
};

/**
 * Retry mechanism for attaching event listeners when function is not immediately available
 * Now supports .once and .prevent modifiers
 */
EventifyDOM.retryEventListenerAttachment = function (
  el,
  eventType,
  funcPath,
  parsedAttr,
  retries = 0,
) {
  var { RETRY_DELAY, MAX_RETRIES, INSTANCE } = this.getDefaultVars();

  const resolvedFunc = INSTANCE.resolveFunction(funcPath);
  if (resolvedFunc) {
    // Function is now available, attach the listener
    const handler = function (event) {
      // Handle .prevent modifier
      if (parsedAttr.hasPrevent && event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }

      // Handle .once modifier
      if (parsedAttr.hasOnce) {
        const onceKey = `${parsedAttr.eventType}_${funcPath}`;
        if (!INSTANCE.onceExecuted.has(el)) {
          INSTANCE.onceExecuted.set(el, new Set());
        }
        if (INSTANCE.onceExecuted.get(el).has(onceKey)) {
          return; // Already executed once, skip
        }
        INSTANCE.onceExecuted.get(el).add(onceKey);
      }

      INSTANCE.executeFunction(resolvedFunc, el, event);
    };

    el.addEventListener(eventType, handler, { passive: !parsedAttr.hasPrevent });

    // Handle attribute cleanup after successful attachment
    const attrName = `data-on.${eventType}${parsedAttr.hasOnce ? ".once" : ""}${parsedAttr.hasPrevent ? ".prevent" : ""}`;
    INSTANCE.handleAttributeAfterAttachment(el, attrName, funcPath);
  } else if (retries < MAX_RETRIES) {
    // Retry after delay
    setTimeout(function () {
      INSTANCE.retryEventListenerAttachment(el, eventType, funcPath, parsedAttr, retries + 1);
    }, RETRY_DELAY);
  } else {
    console.error(
      `Function ${funcPath} not found after ${MAX_RETRIES} retries. Event listener for ${eventType} not attached.`,
    );
  }
};

/**
 * Sets up MutationObserver to watch for dynamically added elements
 * and process their data-on attributes immediately upon insertion.
 * Handles all attribute types: ready, click, mouseover, mouseout, blur, input, change, keydown, keyup, and viewport.
 * Now supports .once and .prevent modifiers.
 * OPTIMIZED: Uses RAF for better performance with many mutations.
 */
EventifyDOM.setupMutationObserver = function () {
  var { INSTANCE } = this.getDefaultVars();

  if (INSTANCE.mutationObserver) return; // Already set up

  let rafId = null;
  let pendingNodes = [];

  INSTANCE.mutationObserver = new MutationObserver(function (mutations) {
    // Collect all added nodes
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          pendingNodes.push(node);
        }
      });
    });

    // Process nodes in next frame for better performance
    if (rafId) return; // Already scheduled

    rafId = requestAnimationFrame(function () {
      rafId = null;

      // Process all pending nodes
      pendingNodes.forEach(function (node) {
        INSTANCE.processDynamicElement(node);

        // Also check child elements for any data-on attributes
        const childElements = node.querySelectorAll(INSTANCE.DATA_ON_SELECTOR);
        childElements.forEach(function (childEl) {
          INSTANCE.processDynamicElement(childEl);
        });
      });

      pendingNodes = []; // Clear processed nodes
    });
  });

  // Start observing the entire document for new elements
  INSTANCE.mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

/**
 * Process a dynamically added element for all data-on attributes.
 * Handles ready, click, mouseover, mouseout, blur, input, change, keydown, keyup, and viewport attributes.
 * For data-on.ready, executes immediately since DOM is already ready.
 * Now supports .once and .prevent modifiers.
 * OPTIMIZED: Single-pass attribute processing.
 *
 * @param {HTMLElement} el - The dynamically added element to process.
 */
EventifyDOM.processDynamicElement = function (el) {
  var { INSTANCE } = this.getDefaultVars();

  const attributes = Array.from(el.attributes);
  const viewportElements = [];

  attributes.forEach(function (attr) {
    if (!attr.name.startsWith("data-on.")) return;

    const parsedAttr = INSTANCE.parseAttributeName(attr.name);
    if (!parsedAttr) return;

    const funcPath = attr.value;
    if (!funcPath || !funcPath.trim()) return;

    if (parsedAttr.eventType === "ready") {
      // Process data-on.ready immediately for dynamic elements (DOM is already ready)
      if (!INSTANCE.processedElements.has(el)) {
        INSTANCE.processedElements.add(el);
        INSTANCE.retryFunctionCall(
          el,
          funcPath,
          function (success) {
            if (success) {
              // For ready events, attachment and execution are the same
              INSTANCE.handleAttributeAfterAttachment(el, attr.name, funcPath);
            }
          },
          0,
          null,
          parsedAttr,
        );
      }
    } else if (parsedAttr.isViewport) {
      // Collect viewport elements for batch processing
      viewportElements.push({
        el,
        funcPath,
        attrName: attr.name,
        parsedAttr,
        threshold: parsedAttr.threshold,
      });
    } else {
      // Process event listeners only once per element+attribute
      const eventKey = `${attr.name}`;
      if (!INSTANCE.eventListenersAdded.has(el)) {
        INSTANCE.eventListenersAdded.set(el, new Set());
      }
      if (!INSTANCE.eventListenersAdded.get(el).has(eventKey)) {
        INSTANCE.eventListenersAdded.get(el).add(eventKey);

        const success = INSTANCE.addEventListenerOnce(
          el,
          parsedAttr.eventType,
          funcPath,
          parsedAttr,
        );
        if (success) {
          INSTANCE.handleAttributeAfterAttachment(el, attr.name, funcPath);
        }
      }
    }
  });

  // Process viewport elements
  if (viewportElements.length > 0) {
    INSTANCE.processDynamicViewportElements(viewportElements);
  }
};

/**
 * Process viewport-related attributes for a dynamically added element.
 * Now supports .once modifier for viewport events.
 *
 * @param {Array} viewportElements - Array of viewport element data.
 */
EventifyDOM.processDynamicViewportElements = function (viewportElements) {
  var { INSTANCE } = this.getDefaultVars();

  // Group by threshold for efficient observer creation
  const thresholdGroups = new Map();

  viewportElements.forEach(function (elementData) {
    const threshold = elementData.threshold;
    if (!thresholdGroups.has(threshold)) {
      thresholdGroups.set(threshold, []);
    }
    thresholdGroups.get(threshold).push(elementData);
  });

  // Create observers for each threshold group
  thresholdGroups.forEach(function (elements, threshold) {
    INSTANCE.createObserver(threshold, elements);
  });
};

/**
 * Retry mechanism to check if the function exists before calling it.
 * Enhanced with function caching for better performance.
 * Now supports .once and .prevent modifiers.
 *
 * @param {HTMLElement} el - The element to check.
 * @param {string} funcPath - The function path to check.
 * @param {function} callback - The callback function to call after retry (pass success state).
 * @param {number} retries - The current retry count.
 * @param {Event} [event] - The optional event object for event handlers.
 * @param {Object} [parsedAttr] - The parsed attribute information with modifiers.
 */
EventifyDOM.retryFunctionCall = function (
  el,
  funcPath,
  callback,
  retries = 0,
  event = null,
  parsedAttr = null,
) {
  var { RETRY_DELAY, MAX_RETRIES, INSTANCE } = this.getDefaultVars();

  // Check cache first for better performance
  if (INSTANCE.functionCache.has(funcPath)) {
    const cachedFunc = INSTANCE.functionCache.get(funcPath);
    if (cachedFunc) {
      // Handle .once modifier
      if (parsedAttr && parsedAttr.hasOnce) {
        const onceKey = `${parsedAttr.eventType || "ready"}_${funcPath}`;
        if (!INSTANCE.onceExecuted.has(el)) {
          INSTANCE.onceExecuted.set(el, new Set());
        }
        if (INSTANCE.onceExecuted.get(el).has(onceKey)) {
          callback(true); // Consider it successful but skip execution
          return;
        }
        INSTANCE.onceExecuted.get(el).add(onceKey);
      }

      // Handle .prevent modifier (only relevant for events with event object)
      if (
        parsedAttr &&
        parsedAttr.hasPrevent &&
        event &&
        typeof event.preventDefault === "function"
      ) {
        event.preventDefault();
      }

      INSTANCE.executeFunction(cachedFunc, el, event);
      callback(true);
      return;
    } else if (cachedFunc === null) {
      // Previously failed function, don't retry
      callback(false);
      return;
    }
  }

  // Check if the function is available
  const func = INSTANCE.resolveFunction(funcPath);
  if (func) {
    INSTANCE.functionCache.set(funcPath, func); // Cache the resolved function

    // Handle .once modifier
    if (parsedAttr && parsedAttr.hasOnce) {
      const onceKey = `${parsedAttr.eventType || "ready"}_${funcPath}`;
      if (!INSTANCE.onceExecuted.has(el)) {
        INSTANCE.onceExecuted.set(el, new Set());
      }
      if (INSTANCE.onceExecuted.get(el).has(onceKey)) {
        callback(true); // Consider it successful but skip execution
        return;
      }
      INSTANCE.onceExecuted.get(el).add(onceKey);
    }

    // Handle .prevent modifier (only relevant for events with event object)
    if (
      parsedAttr &&
      parsedAttr.hasPrevent &&
      event &&
      typeof event.preventDefault === "function"
    ) {
      event.preventDefault();
    }

    INSTANCE.executeFunction(func, el, event);
    callback(true); // Success, function is found and called
  } else if (retries < MAX_RETRIES) {
    // Retry calling the function until it's available or maximum retries are reached
    setTimeout(function () {
      INSTANCE.retryFunctionCall(el, funcPath, callback, retries + 1, event, parsedAttr);
    }, RETRY_DELAY);
  } else {
    console.error(`Function ${funcPath} not found after ${MAX_RETRIES} retries.`);
    INSTANCE.functionCache.set(funcPath, null); // Cache the failure to avoid future retries
    callback(false); // Failure, function not found
  }
};

/**
 * Optimized function resolution that handles both global functions and object methods.
 * Enhanced with early returns and path validation.
 *
 * @param {string} funcPath - The function path in the form "function" or "Object.Method".
 * @returns {Object|null} - Resolved function object or null if not available.
 */
EventifyDOM.resolveFunction = function (funcPath) {
  // Early validation
  if (!funcPath || typeof funcPath !== "string") return null;

  if (funcPath.includes(".")) {
    const parts = funcPath.split(".");
    let obj = window;

    // Traverse the object path efficiently
    for (let i = 0; i < parts.length - 1; i++) {
      obj = obj[parts[i]];
      if (typeof obj !== "object" || obj === null) return null;
    }

    const method = obj[parts[parts.length - 1]];
    return typeof method === "function" ? { obj, method, isMethod: true } : null;
  } else {
    const func = window[funcPath];
    return typeof func === "function" ? { func, isMethod: false } : null;
  }
};

/**
 * Execute resolved function with proper context and arguments.
 *
 * @param {Object} resolvedFunc - The resolved function object from resolveFunction.
 * @param {HTMLElement} el - The element to pass as an argument to the function.
 * @param {Event} [event] - The optional event object for event handlers.
 */
EventifyDOM.executeFunction = function (resolvedFunc, el, event = null) {
  var { $ } = this.getDefaultVars();

  // Convert to jQuery object if jQuery is available
  el = $ ? $(el) : el;

  try {
    if (resolvedFunc.isMethod) {
      resolvedFunc.method.call(resolvedFunc.obj, el, event);
    } else {
      resolvedFunc.func(el, event);
    }
  } catch (error) {
    console.error("Error executing function:", error);
  }
};

/**
 * Creates an IntersectionObserver for elements with a specific threshold.
 * The observer fires when the element's visibility crosses the defined threshold.
 * Optimized to handle multiple elements with the same threshold using a single observer.
 * Now supports .once modifier for viewport events.
 *
 * @param {float} threshold - The visibility threshold to trigger the observer.
 * @param {Array} elementData - Array of element data objects with el, funcPath, attrName, and parsedAttr.
 */
EventifyDOM.createObserver = function (threshold, elementData) {
  var { INSTANCE } = this.getDefaultVars();

  if (!INSTANCE.thresholdObservers[threshold]) {
    INSTANCE.thresholdObservers[threshold] = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (entry.intersectionRatio >= threshold) {
            // Find element data more efficiently
            let elementInfo = null;
            for (let i = 0; i < elementData.length; i++) {
              if (elementData[i].el === entry.target) {
                elementInfo = elementData[i];
                break;
              }
            }

            if (!elementInfo) return;

            INSTANCE.retryFunctionCall(
              entry.target,
              elementInfo.funcPath,
              function (success) {
                if (success) {
                  // For viewport events, attachment happens when observer is created,
                  // but we handle attribute cleanup after first successful execution
                  INSTANCE.handleAttributeAfterAttachment(
                    entry.target,
                    elementInfo.attrName,
                    elementInfo.funcPath,
                  );
                }
              },
              0,
              null,
              elementInfo.parsedAttr,
            );

            // Always unobserve once triggered (natural "once" behavior for viewport events)
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: threshold, // The dynamic threshold value
      },
    );
  }

  // Observe all elements for this threshold
  elementData.forEach(function (data) {
    INSTANCE.thresholdObservers[threshold].observe(data.el);
  });
};

// Legacy methods for backward compatibility (optimized)

/**
 * Handles the `data-on.ready` attribute.
 * DEPRECATED: Use processAllAttributes() for better performance.
 */
EventifyDOM.attrReady = function () {
  console.warn("EventifyDOM.attrReady() is deprecated. Use processAllAttributes() instead.");
  this.processAllAttributes();
};

/**
 * Handles the `data-on.click` attribute.
 * DEPRECATED: Use processAllAttributes() for better performance.
 */
EventifyDOM.attrClick = function () {
  console.warn("EventifyDOM.attrClick() is deprecated. Use processAllAttributes() instead.");
  this.processAllAttributes();
};

/**
 * Handles the `data-on.mouseover` attribute.
 * DEPRECATED: Use processAllAttributes() for better performance.
 */
EventifyDOM.attrMouseover = function () {
  console.warn("EventifyDOM.attrMouseover() is deprecated. Use processAllAttributes() instead.");
  this.processAllAttributes();
};

/**
 * Handles the `data-on.mouseout` attribute.
 * DEPRECATED: Use processAllAttributes() for better performance.
 */
EventifyDOM.attrMouseout = function () {
  console.warn("EventifyDOM.attrMouseout() is deprecated. Use processAllAttributes() instead.");
  this.processAllAttributes();
};

/**
 * Handles the `data-on.inviewport` attributes.
 * DEPRECATED: Use processAllAttributes() for better performance.
 */
EventifyDOM.attrInViewport = function () {
  console.warn("EventifyDOM.attrInViewport() is deprecated. Use processAllAttributes() instead.");
  this.processAllAttributes();
};

/**
 * Checks if the function is available in a namespaced object (e.g., Obj.func).
 * Maintained for backward compatibility but now uses optimized resolveFunction.
 *
 * @param {string} funcPath - The function path in the form "Object.Method".
 * @returns {boolean} - Whether the function is available.
 */
EventifyDOM.isMethodAvailable = function (funcPath) {
  return !!this.resolveFunction(funcPath);
};

/**
 * Calls the function when it's available.
 * Maintained for backward compatibility but now uses optimized executeFunction.
 *
 * @param {string} funcPath - The function path to call.
 * @param {HTMLElement} el - The element to pass as an argument to the function.
 * @param {Event} [event] - The optional event object for event handlers.
 */
EventifyDOM.callFunction = function (funcPath, el, event = null) {
  const resolvedFunc = this.resolveFunction(funcPath);
  if (resolvedFunc) {
    this.executeFunction(resolvedFunc, el, event);
  }
};

// Initialize the triggerer after DOM is ready
// EventifyDOM.init();

export default EventifyDOM;
